// /Users/joshua.moore1153/Desktop/Sinclair Apps/spcore-v4/src/components/AdminHooks/fireBaseHook.js
import { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query, // <-- Import query
  orderBy, // <-- Import orderBy
} from "firebase/firestore";
import { app as firebaseApp } from "./firebase";

const db = getFirestore(firebaseApp);

// Helper function to format Date objects or Firestore Timestamps to MM-DD-YYYY
const formatDate = (date) => {
  if (!date) return "";
  // Firestore Timestamp
  if (typeof date === "object" && date.seconds) {
    const d = new Date(date.seconds * 1000);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
  // JS Date
  if (date instanceof Date) {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
  // String
  if (typeof date === "string") {
    // Try to parse as date
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
    return date;
  }
  return date;
};

// --- MODIFIED Hook Signature ---
const useFirestore = (
  collectionName,
  orderByField = null, // Optional: Field to order by
  orderByDirection = "asc", // Optional: 'asc' or 'desc'
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the base collection reference
  const baseCollectionRef = useCallback(
    () => (collectionName ? collection(db, collectionName) : null),
    [collectionName],
  );

  // Fetch Data (List) with Real-time Updates
  useEffect(() => {
    // Skip fetch if no collection name provided (lazy loading pattern)
    if (!collectionName) {
      setLoading(false);
      setData([]);
      return;
    }

    setLoading(true);
    setError(null);

    // --- Build the Query ---
    let queryRef = baseCollectionRef(); // Start with the base collection
    if (orderByField) {
      // If an orderByField is provided, create a query with ordering
      // Ensure orderByDirection is either 'asc' or 'desc'
      const direction = orderByDirection === "desc" ? "desc" : "asc";
      queryRef = query(baseCollectionRef(), orderBy(orderByField, direction));
      console.log(`Firestore query ordered by: ${orderByField} ${direction}`); // For debugging
    } else {
      console.log(`Firestore query using default (unordered)`); // For debugging
    }
    // --- End Query Build ---

    // Use onSnapshot with the potentially ordered queryRef
    const unsubscribe = onSnapshot(
      queryRef, // Use the constructed queryRef here
      (querySnapshot) => {
        const items = [];
        querySnapshot.forEach((doc) => {
          const docData = doc.data();
          const processedData = {};

          // Iterate over document fields to find and format Timestamps AFTER sorting
          for (const key in docData) {
            if (docData.hasOwnProperty(key)) {
              const value = docData[key];
              // For created_at, updatedAt, and expireDate, keep as Firestore Timestamp for proper date handling
              if (
                (key === "created_at" ||
                  key === "updatedAt" ||
                  key === "expireDate") &&
                value instanceof Timestamp
              ) {
                processedData[key] = value;
              } else if (value instanceof Timestamp) {
                processedData[key] = formatDate(value.toDate());
              } else {
                processedData[key] = value;
              }
            }
          }

          items.push({ id: doc.id, ...processedData });
        });
        setData(items);
        setLoading(false);
      },
      (err) => {
        console.error(`Error fetching collection ${collectionName}:`, err);
        setError(
          `Failed to fetch data from ${collectionName}. Please try again later.`,
        );
        setLoading(false);
      },
    );

    // Cleanup listener
    return () => unsubscribe();
    // --- MODIFIED Dependency Array ---
    // Re-run effect if collectionName, orderByField, or orderByDirection changes
  }, [collectionName, baseCollectionRef, orderByField, orderByDirection]);

  // Add Data (Create)
  const addData = useCallback(
    async (newItemData) => {
      // Always add a 'created_at' and 'updatedAt' timestamp if not present
      const dataToAdd = {
        ...newItemData,
        created_at: newItemData.created_at || Timestamp.now(),
        updatedAt: newItemData.updatedAt || Timestamp.now(),
      };
      try {
        const docRef = await addDoc(baseCollectionRef(), dataToAdd);
        console.log("Document written with ID: ", docRef.id);
        return docRef;
      } catch (err) {
        console.error(`Error adding document to ${collectionName}:`, err);
        setError(`Failed to add item to ${collectionName}.`);
        throw err;
      }
    },
    [collectionName, baseCollectionRef],
  );

  // Update Data (Edit)
  const updateData = useCallback(
    async (docId, updatedItemData) => {
      if (!docId) {
        const errMsg = "Error: docId is required for updating.";
        console.error(errMsg);
        setError(errMsg);
        throw new Error(errMsg);
      }
      const docRef = doc(db, collectionName, docId);
      try {
        // --- Suggestion: Add an 'updatedAt' timestamp automatically ---
        const dataToUpdate = {
          ...updatedItemData,
          updatedAt: Timestamp.now(), // Add server timestamp on update
        };
        // --- End Suggestion ---
        await updateDoc(docRef, dataToUpdate); // Use dataToUpdate
        console.log("Document successfully updated: ", docId);
      } catch (err) {
        console.error(
          `Error updating document ${docId} in ${collectionName}:`,
          err,
        );
        setError(`Failed to update item in ${collectionName}.`);
        throw err;
      }
    },
    [collectionName], // db is stable
  );

  // Delete Data
  const deleteData = useCallback(
    async (docId) => {
      if (!docId) {
        const errMsg = "Error: docId is required for deleting.";
        console.error(errMsg);
        setError(errMsg);
        throw new Error(errMsg);
      }
      const docRef = doc(db, collectionName, docId);
      try {
        await deleteDoc(docRef);
        console.log("Document successfully deleted: ", docId);
      } catch (err) {
        console.error(
          `Error deleting document ${docId} from ${collectionName}:`,
          err,
        );
        setError(`Failed to delete item from ${collectionName}.`);
        throw err;
      }
    },
    [collectionName], // db is stable
  );

  return { data, loading, error, addData, updateData, deleteData };
};

export default useFirestore;
